import { ReactNode } from 'react';
import { UUID } from 'v2/core/entities/uuid';
import { create, StoreApi } from 'zustand';
import createContext from 'zustand/context';

export interface AudioSnippetTime {
  startTime: number;
  endTime: number;
}
export interface FeedbackListStoreProp {
  isPlaying?: boolean;
  audioDuration?: number;
  audioSeek?: number;
  audioRate?: number;
  selectedSeekJumpTime?: number | undefined;
  currentPlayingMsgIndex?: number | undefined;
  snippetTimeToPlay?: AudioSnippetTime;
  isTranscriptPopoverOpened?: boolean;
  isTranscriptPopoverExpanded?: boolean;
  summaryTimeToPlay?: AudioSnippetTime;
  audioHighlightLabelID?: UUID;
  selectedAudioSnippet?: AudioSnippetTime;
}

export interface FeedbackListStore {
  feedbacks: Record<UUID, FeedbackListStoreProp>;
  setIsPlaying: (isPlaying: boolean, feedbackID: UUID) => void;
  setAudioDuration: (time: number, feedbackID: UUID) => void;
  setAudioSeek: (time: number, feedbackID: UUID) => void;
  setAudioRate: (rate: number, feedbackID: UUID) => void;
  setSelectedSeekJumpTime: (time: number | undefined, feedbackID: UUID) => void;
  setSnippetTimeToPlay: (
    time: { startTime: number; endTime: number } | undefined,
    feedbackID: UUID,
  ) => void;
  setCurrentPlayingMsgIndex: (index: number, feedbackID: UUID) => void;
  setIsTranscriptPopoverOpened: (isOpened: boolean, feedbackID: UUID) => void;
  setIsTranscriptPopoverExpanded: (
    isExpanded: boolean,
    feedbackID: UUID,
  ) => void;
  setSummaryTimeToPlay: (
    time: { startTime: number; endTime: number } | undefined,
    feedbackID: UUID,
  ) => void;
  setAudioHighlightLabelID: (
    labelID: UUID | undefined,
    feedbackID: UUID,
  ) => void;
  setSelectedSnippetAudioSnippet: (
    snippet: AudioSnippetTime,
    feedbackID: UUID,
  ) => void;
}

const feedbackListStoreCreatorProps = (
  set: StoreApi<FeedbackListStore>['setState'],
): FeedbackListStore => ({
  feedbacks: {},
  setIsPlaying: (isPlaying: boolean, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          isPlaying,
        },
      },
    }));
  },
  setAudioDuration: (time: number, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          audioDuration: time,
        },
      },
    }));
  },
  setAudioSeek: (time: number, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          audioSeek: time,
        },
      },
    }));
  },
  setAudioRate: (rate: number, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          audioRate: rate,
        },
      },
    }));
  },
  setSelectedSeekJumpTime: (time: number | undefined, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          selectedSeekJumpTime: time,
        },
      },
    }));
  },
  setSnippetTimeToPlay: (
    time: AudioSnippetTime | undefined,
    feedbackID: UUID,
  ) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          snippetTimeToPlay: time,
        },
      },
    }));
  },
  setCurrentPlayingMsgIndex: (index: number, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          currentPlayingMsgIndex: index,
        },
      },
    }));
  },
  setIsTranscriptPopoverOpened: (isOpen: boolean, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          isTranscriptPopoverOpened: isOpen,
        },
      },
    }));
  },
  setIsTranscriptPopoverExpanded: (isExpanded: boolean, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          isTranscriptPopoverExpanded: isExpanded,
        },
      },
    }));
  },
  setSummaryTimeToPlay: (
    time: AudioSnippetTime | undefined,
    feedbackID: UUID,
  ) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          summaryTimeToPlay: time,
        },
      },
    }));
  },
  setAudioHighlightLabelID: (labelID: UUID | undefined, feedbackID: UUID) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          audioHighlightLabelID: labelID,
        },
      },
    }));
  },
  setSelectedSnippetAudioSnippet: (
    snippet: AudioSnippetTime,
    feedbackID: UUID,
  ) => {
    set((state) => ({
      feedbacks: {
        ...state.feedbacks,
        [feedbackID]: {
          ...state.feedbacks?.[feedbackID],
          selectedAudioSnippet: snippet,
        },
      },
    }));
  },
});

const feedbackListStoreCreator = () =>
  create<FeedbackListStore>(feedbackListStoreCreatorProps);

const { Provider, useStore: useFeedbackListStore } =
  createContext<StoreApi<FeedbackListStore>>();

export const FeedbackListStoreProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  return <Provider createStore={feedbackListStoreCreator}>{children}</Provider>;
};

export default useFeedbackListStore;
